import { IProductWarranty } from 'modules/inventory/product/interfaces/IProductWarranty';
import { normalizeWarranty } from 'modules/inventory/product/utils/product-warranty';
import { ApiClientService, EntityApiService, RequestConfig, SearchResponseType } from '@dfl/react-security';
import { IAddress } from 'lib/dfl-address-form-mui/interfaces';
import { IProduct } from 'modules/inventory/product/interfaces/IProduct';
import { IProductPriceDetails } from 'modules/inventory/product/interfaces/IProductPriceDetails';
import { IProductCreate } from 'modules/inventory/product/interfaces/IProductCreate';
import { IVariantsStockByParent } from 'modules/inventory/product/interfaces/IVariantStock';
import { mapperObjectToArrayTags } from 'modules/inventory/settings/tags/services/tags-mapper';
import { RELATED_PRODUCTS_ACTION } from '../constants/related-products.enum';
import { VariantMigrationAssignment } from 'modules/inventory/product/hooks/useVariantMigrationForm';

class ProductService extends EntityApiService<IProduct> {
  searchClean = (params?: any, config?: RequestConfig): Promise<SearchResponseType<IProduct>> => {
    const projections = {
      owner: 0,
      barCode: 0,
      description: 0,
      lastPrice: 0,
      updatedAt: 0,
      rating: 0,
      referenceCode: 0,
      score: 0,
      seo: 0,
      shippingSettings: 0,
      shopSlug: 0,
      slug: 0,
      tags: 0,
      related: 0,
      id: 0,
    };
    return this.search({ ...params, projections }, config);
  };

  searchWithStock = (params?: any, config?: RequestConfig) => {
    const projections = {
      owner: 0,
      barCode: 0,
      description: 0,
      lastPrice: 0,
      updatedAt: 0,
      rating: 0,
      referenceCode: 0,
      score: 0,
      seo: 0,
      shippingSettings: 0,
      shopSlug: 0,
      slug: 0,
      tags: 0,
      related: 0,
      id: 0,
    };
    return this.handleResponse(
      ApiClientService.post(this.getPath('/search-with-stock'), { ...params, projections }, config),
    );
  };

  getOne = (productId: string, config?: RequestConfig | undefined) => {
    return this.handleResponse(
      ApiClientService.get(this.getPath(`/${productId}`), config).then((data) => {
        // const productTag = mapperObjectToArrayTags(data?.data?.tags?.product);
        const supplierTag = mapperObjectToArrayTags(data?.data?.tags?.supplier);
        return {
          ...data,
          data: {
            ...data.data,
            tags: {
              product: data?.data?.tags?.product,
              supplier: supplierTag,
            },
          },
        };
      }),
    );
  };

  updateAddressInfo = (productId: string, params: IAddress, config?: RequestConfig): Promise<IAddress> => {
    return this.handleResponse(ApiClientService.patch(this.getPath(`/${productId}/address`), params, config));
  };

  updateStatus = (productId: string, status: boolean): any => {
    return ApiClientService.patch(this.getPath(`/${productId}/visibility`), {
      visible: status,
    });
  };

  recalcSeoAudit = (productId: string): any => {
    return ApiClientService.post(this.getPath(`/${productId}/recalc-seo-audit`), {});
  };

  updateTop = (productId: string, top: boolean): any => {
    return ApiClientService.patch(this.getPath(`/${productId}/internal-seo`), { top });
  };

  productCode = (code: string, warehouse: string): any => {
    return ApiClientService.get(
      this.getPath(`/code/${code}`) /* {
      warehouse,
    } */,
    );
  };

  updatePrice = (productId: string, priceProduct: IProductPriceDetails): any => {
    return ApiClientService.patch(this.getPath(`/${productId}/price`), {
      priceDetails: priceProduct,
    });
  };

  updateRelatedProducts = (productId: string, relatedProducts: string[], action: RELATED_PRODUCTS_ACTION): any => {
    return ApiClientService.patch(this.getPath(`/${productId}/related-products/${action}`), {
      ids: relatedProducts,
    });
  };

  searchRelatedProducts = (productId: string, params?: any, config?: any): any => {
    return this.handleResponse(
      ApiClientService.post(this.getPath(`/${productId}/related-products/search`), params, config),
    );
  };

  // update shipping info
  updateShippingInfo = (id: string, payload: any): any => {
    if (id) {
      return ApiClientService.patch(this.getPath(`/${id}/shipping-info`), payload);
    }
    throw new Error('You must be inside a _id');
  };

  // update shipping time
  updateShippingTime = (payload: Partial<IProductCreate>): any => {
    const { _id, shippingSettings } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/shipping-time`), {
        ...shippingSettings?.estimatedTime,
      });
    }
    throw new Error('You must be inside a _id');
  };

  // update rules
  updateRules = (payload: Partial<IProductCreate>): any => {
    const { _id, rules } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/rules`), {
        ...rules,
      });
    }
    throw new Error('You must be inside a _id');
  };

  updateWarranty = (productId: string, warranty: IProductWarranty) =>
    this.handleResponse(ApiClientService.patch(this.getPath(`/${productId}`), { warranty: normalizeWarranty(warranty) }));

  updateFiles = (productId: string, files: NonNullable<IProductCreate['files']>) => {
    return this.handleResponse(ApiClientService.patch(this.getPath(`/${productId}`), { files }));
  };

  // update media
  updateMedia = (payload: Partial<IProductCreate>): any => {
    const { _id, media } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/media`), {
        media,
      });
    }
    throw new Error('You must be inside a _id');
  };

  // update score
  updateScore = (payload: Partial<IProductCreate>): any => {
    const { _id, ...rest } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/score`), {
        ...rest,
      });
    }
    throw new Error('You must be inside a _id');
  };

  // update score
  updateSeo = (payload: Partial<IProductCreate>): any => {
    const { _id, seo, slug } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/seo`), {
        ...seo,
        slug,
      });
    }
    throw new Error('You must be inside a _id');
  };

  // update replenishment
  updateReplenishment = (id: string, payload: any): any => {
    if (id) {
      return ApiClientService.patch(this.getPath(`/${id}/replenishment`), payload);
    }
    throw new Error('You must be inside a _id');
  };

  // update replenishment in bulk
  updateReplenishmentMany = (filters: any, search: any, value: any): any => {
    return ApiClientService.patch(this.getPath('/-/replenishment'), { search, filters, value });
  };

  // update providers
  updateProviders = (payload: Partial<IProductCreate>): any => {
    const { _id, providers } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/providers`), {
        providers,
      });
    }
    throw new Error('You must be inside a _id');
  };

  // update tags
  updateTags = (payload: Partial<IProductCreate>): any => {
    const { _id, tags } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/tags`), { tags });
    }
    throw new Error('You must be inside a _id');
  };

  // delete in bulk
  deleteMany = (ids: string[]): any => {
    if (ids) {
      return ApiClientService.patch(this.getPath('/bulk/remove'), {
        ids,
      });
    }
    throw new Error('You must be inside a ids array');
  };

  // change visibility in bulk
  changeVisibilityMany = ({ ids, visible }: { ids: string[]; visible: string | boolean }): any => {
    if (ids) {
      return ApiClientService.patch(this.getPath('/bulk/visibility'), {
        ids,
        visible,
      });
    }
    throw new Error('You must be inside a ids array and visible');
  };

  changeCategoryMany = ({ ids, category }: { ids: string[]; category: string }): any => {
    if (ids) {
      return ApiClientService.patch(this.getPath('/bulk/category'), {
        ids,
        category,
      });
    }
    throw new Error('You must be inside a ids array and visible');
  };

  addLabels = ({ search, filters, value }: { search: string; filters: any; value: Array<{ labelId: string; startDate?: string | null; endDate?: string | null }> }): any => {
    return ApiClientService.put(this.getPath('/-/label-configurations'), {
      search,
      filters,
      value,
    });
  };

  changeVisibilityManyStock = (
    productId: string,
    { ids, visible }: { ids: string[]; visible: string | boolean },
  ): any => {
    if (ids) {
      return ApiClientService.patch(this.getPath(`/${productId}/stock/bulk/visibility`), {
        ids,
        visible,
      });
    }
    throw new Error('You must be inside a ids array and visible');
  };

  // change visibility in bulk
  changeScoreMany = ({ ids, score }: { ids: string[]; score: number }): any => {
    if (ids) {
      return ApiClientService.patch(this.getPath('/bulk/score'), {
        ids,
        score,
      });
    }
    throw new Error('You must be inside a ids array and score');
  };

  // search warehouse whit stock
  searchWarehouseStock = (productId: string, params?: any, config?: any): any => {
    return this.handleResponse(ApiClientService.post(this.getPath(`/${productId}/stock/search`), params, config));
  };

  // Group stock view for a variant parent: totals + per-variant/per-warehouse breakdown.
  searchVariantsStockByParent = (parentId: string, params: any = {}, config?: any): Promise<IVariantsStockByParent> => {
    return this.handleResponse(
      ApiClientService.post(this.getPath(`/${parentId}/stock/variants/search`), params, config),
    ) as Promise<IVariantsStockByParent>;
  };

  // calculate preview discount
  calculateDiscount = (params?: any, config?: any): any => {
    return this.handleResponse(ApiClientService.post(this.getPath('/price/preview'), params, config));
  };

  // update internalSeo
  updateInternalSeo = (payload: Partial<IProductCreate>): any => {
    const { _id, internalSeo } = payload;
    if (_id) {
      return ApiClientService.patch(this.getPath(`/${_id}/internal-seo`), internalSeo);
    }
    throw new Error('You must be inside a _id');
  };

  updateDescriptionAndSeo = async (
    product: {
      _id: string;
      description: string;
    },
    seo: { title: string; description: string },
    check: {
      description: boolean;
      seo_title: boolean;
      seo_description: boolean;
    },
    item: {
      seo: {
        name: string;
        description: string;
      };
    },
  ) => {
    let descRes, seoRes;

    if (check.description) {
      descRes = await this.saveOrUpdate(product);
    }
    if (check.seo_title || check?.seo_description) {
      seoRes = await this.updateSeo({
        _id: product?._id,
        seo: {
          ...(check.seo_title ? { name: seo?.title } : { name: item?.seo?.name || '' }),
          ...(check.seo_description
            ? { description: seo?.description }
            : { description: item?.seo?.description || '' }),
        },
      });
    }

    return {
      product: descRes,
      seo: seoRes,
    };
  };

  searchByMargin = (params?: any, config?: RequestConfig) => {
    return this.handleResponse(
      ApiClientService.post(
        this.getPath('/search'),
        {
          ...params,
          sort: {
            finalPrice: 'desc',
          },
          projections: {
            owner: 0,
            barCode: 0,
            description: 0,
            lastPrice: 0,
            updatedAt: 0,
            rating: 0,
            referenceCode: 0,
            score: 0,
            seo: 0,
            shippingSettings: 0,
            shopSlug: 0,
            slug: 0,
            tags: 0,
            related: 0,
            id: 0,
          },
        },
        config,
      ),
    );
  };

  cloneProduct = (product: string, params: any, config?: RequestConfig) => {
    return this.handleResponse(ApiClientService.post(this.getPath(`/${product}/clone`), params, config));
  };

  promoteToGroup = (id: string, dto: any, config?: RequestConfig) => {
    return this.handleResponse(ApiClientService.post(this.getPath(`/${id}/variant-group`), dto, config));
  };

  updateVariantAxes = (id: string, variantAxes: any[]): any => {
    return ApiClientService.patch(this.getPath(`/${id}/variant-axes`), { variantAxes });
  };

  generateVariants = (id: string, dto: any, config?: RequestConfig) => {
    return this.handleResponse(ApiClientService.post(this.getPath(`/${id}/variants`), dto, config));
  };

  findVariants = (id: string, config?: RequestConfig) => {
    return this.handleResponse(ApiClientService.get(this.getPath(`/${id}/variants`), config));
  };

  detachVariant = (id: string): any => {
    return ApiClientService.patch(this.getPath(`/${id}/detach-variant`), {});
  };

  migrateVariants = (id: string, dto: { variantAxes: any[]; assignments: VariantMigrationAssignment[] }): Promise<any> =>
    this.handleResponse(ApiClientService.post(this.getPath(`/${id}/variants/migrate`), dto));
}

export default new ProductService('/ms-inventory/api/products');
